import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  text?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'glass' | 'active';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  isLoading?: boolean;
  fullWidth?: boolean;
  component?: React.ElementType; // For using as Link
  to?: string; // For Link component
}

const Button: React.FC<ButtonProps> = ({
  children,
  text,
  variant = 'primary',
  size = 'md',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  isLoading = false,
  fullWidth = false,
  className = '',
  disabled,
  component: Component = 'button',
  to,
  ...props
}) => {
  const getVariantStyles = (variant: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      fontWeight: '600',
      borderRadius: '0.5rem',
      border: 'none',
      cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
      opacity: disabled || isLoading ? 0.5 : 1,
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      outline: 'none',
      width: fullWidth ? '100%' : 'auto',
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyle,
          background: 'linear-gradient(to right, #2563eb, #7c3aed)',
          color: 'white',
          boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.25)',
        };
      case 'secondary':
        return {
          ...baseStyle,
          background: 'rgba(30, 41, 59, 0.8)',
          color: '#dbeafe',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          backdropFilter: 'blur(8px)',
        };
      case 'ghost':
        return {
          ...baseStyle,
          background: 'transparent',
          color: '#94a3b8',
          border: 'none',
        };
      case 'glass':
        return {
          ...baseStyle,
          background: 'rgba(15, 23, 42, 0.8)',
          color: '#dbeafe',
          border: '1px solid rgba(96, 165, 250, 0.2)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        };
      case 'active':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(5, 150, 105, 0.3))',
          border: '1px solid rgba(16, 185, 129, 0.6)',
          color: '#6ee7b7',
          boxShadow: '0 5px 15px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        };
      default:
        return baseStyle;
    }
  };

  const getSizeStyles = (size: string): React.CSSProperties => {
    switch (size) {
      case 'sm':
        return {
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
        };
      case 'md':
        return {
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
        };
      case 'lg':
        return {
          padding: '1rem 2rem',
          fontSize: '1.125rem',
        };
      default:
        return {
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
        };
    }
  };

  const buttonStyle: React.CSSProperties = {
    ...getVariantStyles(variant),
    ...getSizeStyles(size),
  };

  const [style, setStyle] = React.useState(buttonStyle);

  React.useEffect(() => {
    setStyle(getVariantStyles(variant));
  }, [variant]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || isLoading) return;
    
    switch (variant) {
      case 'primary':
        e.currentTarget.style.background = 'linear-gradient(to right, #1d4ed8, #6d28d9)';
        e.currentTarget.style.transform = 'translateY(-1px)';
        break;
      case 'secondary':
        e.currentTarget.style.background = 'rgba(51, 65, 85, 0.8)';
        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
        break;
      case 'ghost':
        e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)';
        e.currentTarget.style.color = '#dbeafe';
        break;
      case 'glass':
        e.currentTarget.style.background = 'rgba(30, 41, 59, 0.9)';
        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
        break;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || isLoading) return;
    
    switch (variant) {
      case 'primary':
        e.currentTarget.style.background = 'linear-gradient(to right, #2563eb, #7c3aed)';
        e.currentTarget.style.transform = 'translateY(0)';
        break;
      case 'secondary':
        e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
        break;
      case 'ghost':
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = '#94a3b8';
        break;
      case 'glass':
        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)';
        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.2)';
        break;
    }
  };

  const buttonContent = (
    <>
      {isLoading ? (
        <div
          style={{
            width: '1rem',
            height: '1rem',
            border: '2px solid transparent',
            borderTop: '2px solid currentColor',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      ) : (
        <>
          {variant === 'active' && <Check size={16} style={{ marginRight: '0.25rem' }} />}
          {LeftIcon && <LeftIcon size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />}
          {children || text}
          {RightIcon && <RightIcon size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />}
        </>
      )}
    </>
  );

  return (
    <Component
      style={style}
      className={className}
      disabled={disabled || isLoading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {buttonContent}
    </Component>
  );
};

export default Button; 